import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  UntypedFormControl,
  UntypedFormGroup,
  NgControl,
} from '@angular/forms';
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { Moment } from 'moment';
import moment from 'moment-timezone';
import { TextInput } from '../text-input';
import { SelectionChange } from '../year-picker/year-picker.component';

@Component({
  selector: 'miq-year-input',
  templateUrl: './year-input.component.html',
  styleUrls: ['./year-input.component.scss'],
  standalone: false,
})
export class YearInputComponent extends TextInput implements OnInit, OnChanges, ControlValueAccessor {
  @Input() placeholder = '';
  @Input() readonly = false;
  @Input() panelClass: string;
  @Input() minDate: string | Moment;
  @Input() maxDate: string | Moment;

  @Output() currentValueChange = new EventEmitter<any>();
  @Output() formControlCreated: EventEmitter<UntypedFormControl> = new EventEmitter<UntypedFormControl>();

  @ViewChild('picker') picker: MatDatepicker<any>;

  protected onTouched: Function;
  private onChange: Function;
  showClearIcon = false;

  constructor(
    @Optional() private controlContainer: ControlContainer,
    @Self() @Optional() private ngControl: NgControl
  ) {
    super();
    if (!!ngControl) {
      ngControl.valueAccessor = this;
    }
    this.onChange = this.onDateChange.bind(this);
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    const { currentValue } = changes;
    if (!currentValue.firstChange && currentValue.currentValue !== currentValue.previousValue) {
      this.fc.setValue(currentValue.currentValue);
    }

    this.updateClearIcon(this.currentValue);
  }

  updateClearIcon(value) {
    this.showClearIcon = !this.disabled && this.readonly && value != null;
  }

  /**
   * fc control is created in initFormControl method which is invoked
   * in super class InputBase by calling initFormControl
   */
  ngOnInit(): void {
    super.ngOnInit();
    this.formControlCreated.emit(this.fc);
  }

  protected initFormControl() {
    this.fc = !!this.ngControl
      ? ((this.controlContainer.control as UntypedFormGroup).controls[this.ngControl.name] as UntypedFormControl)
      : new UntypedFormControl(this.controlValues(), this.controlValidators());
  }

  onSelectionChange(evt: SelectionChange) {
    this.updateClearIcon(evt.year ?? null);
    this.currentValueChange.emit(evt.year ?? null);
  }

  // Required methods by the ControlValueAccessor

  /**
   * Main on change value transformation method. It set a new value to date
   * property also emit transformed date value
   *
   * @param change New value to display
   * @return Transformed date
   */
  onDateChange(_change: MatDatepickerInputEvent<Moment>) {}

  /**
   * Used by Angular to register a function to call on save
   *
   * @param fn Angular function
   */
  registerOnChange(fn: any): void {
    this.onChange = (newValue: string | moment.Moment) => fn(this.onDateChange(newValue as any));
  }

  /**
   * Used by Angular to register a function to call on touched
   *
   * @param fn Angular function
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Used by angular to update value
   *
   * @param date New date value
   */
  writeValue(date: string | moment.Moment): void {
    this.onChange(date);
  }

  /**
   * Used by angular to set control as disabled
   *
   * @param isDisabled Is disabled value
   */
  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.fc.disable();
    } else {
      this.fc.enable();
    }
  }
}
